import axios from "axios";
import toast from "react-hot-toast";

// Base API configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem("kslnk_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem("kslnk_token");
          delete api.defaults.headers.common["Authorization"];

          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          break;

        case 403:
          // Forbidden
          if (data.error?.message) {
            toast.error(data.error.message);
          }
          break;

        case 429:
          // Rate limit exceeded
          toast.error("Çok fazla istek gönderdiniz. Lütfen biraz bekleyin.");
          break;

        case 500:
          // Server error
          toast.error(
            "Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin."
          );
          break;

        default:
          if (data.error?.message) {
            toast.error(data.error.message);
          }
      }
    } else if (error.request) {
      // Network error
      toast.error("İnternet bağlantınızı kontrol edin.");
    } else {
      // Other error
      toast.error("Beklenmeyen bir hata oluştu.");
    }

    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  logout: () => api.post("/auth/logout"),
  getProfile: () => api.get("/auth/me"),
  updateProfile: (profileData) => api.put("/auth/profile", profileData),
};

// Links service
export const linksService = {
  create: (linkData) => api.post("/links/shorten", linkData),
  getAll: (params) => api.get("/links", { params }),
  getById: (id) => api.get(`/links/${id}`),
  update: (id, linkData) => api.put(`/links/${id}`, linkData),
  delete: (id) => api.delete(`/links/${id}`),
  getStats: (id, params) => api.get(`/links/${id}/stats`, { params }),
};

// User service
export const userService = {
  getDashboard: () => api.get("/user/dashboard"),
  getEarnings: (params) => api.get("/user/earnings", { params }),
  requestWithdrawal: (withdrawalData) =>
    api.post("/user/withdrawal", withdrawalData),
  getReferrals: (params) => api.get("/user/referrals", { params }),
  getPayments: (params) => api.get("/user/payments", { params }),
};

// Admin service
export const adminService = {
  getDashboard: () => api.get("/admin/dashboard"),

  // Users management
  getUsers: (params) => api.get("/admin/users", { params }),
  updateUser: (id, userData) => api.put(`/admin/users/${id}`, userData),

  // Links management
  getLinks: (params) => api.get("/admin/links", { params }),

  // Countries management
  updateCountries: (countriesData) =>
    api.put("/admin/countries", countriesData),

  // Payments management
  getPayments: (params) => api.get("/admin/payments", { params }),
  processPayment: (id, paymentData) =>
    api.put(`/admin/payments/${id}`, paymentData),

  // Advertisements management
  getAds: (params) => api.get("/admin/ads", { params }),
  createAd: (adData) => api.post("/admin/ads", adData),
  updateAd: (id, adData) => api.put(`/admin/ads/${id}`, adData),
  deleteAd: (id) => api.delete(`/admin/ads/${id}`),

  // CMS management
  getPages: (params) => api.get("/admin/cms/pages", { params }),
  createPage: (pageData) => api.post("/admin/cms/pages", pageData),
  updatePage: (id, pageData) => api.put(`/admin/cms/pages/${id}`, pageData),
  deletePage: (id) => api.delete(`/admin/cms/pages/${id}`),
};

// CMS service (public)
export const cmsService = {
  getPages: (params) => api.get("/cms/pages", { params }),
  getPage: (slug) => api.get(`/cms/pages/${slug}`),
  getAnnouncements: (params) => api.get("/cms/announcements", { params }),
};

// Utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    } else if (error.message) {
      return error.message;
    } else {
      return "Beklenmeyen bir hata oluştu";
    }
  },

  // Extract data from API response
  extractData: (response) => {
    return response.data?.data || response.data;
  },

  // Check if response is successful
  isSuccessResponse: (response) => {
    return response.data?.success === true;
  },

  // Format pagination data
  formatPagination: (response) => {
    const data = response.data?.data || {};
    return {
      items: data.items || data.links || data.users || data.payments || [],
      pagination: data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNext: false,
        hasPrev: false,
      },
    };
  },
};

// Upload service for file uploads
export const uploadService = {
  uploadFile: async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);

    return api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });
  },
};

// Analytics service
export const analyticsService = {
  track: (event, properties = {}) => {
    // Track events for analytics (Google Analytics, etc.)
    if (typeof gtag !== "undefined") {
      gtag("event", event, properties);
    }
  },

  trackPageView: (path) => {
    if (typeof gtag !== "undefined") {
      gtag("config", process.env.REACT_APP_GA_TRACKING_ID, {
        page_path: path,
      });
    }
  },
};

// WebSocket service for real-time updates
export class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
  }

  connect() {
    const token = localStorage.getItem("kslnk_token");
    if (!token) return;

    const wsUrl = process.env.REACT_APP_WS_URL || "ws://localhost:5000";
    this.ws = new WebSocket(`${wsUrl}?token=${token}`);

    this.ws.onopen = () => {
      console.log("WebSocket connected");
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners(data.type, data.payload);
      } catch (error) {
        console.error("WebSocket message parse error:", error);
      }
    };

    this.ws.onclose = () => {
      console.log("WebSocket disconnected");
      // Reconnect after 5 seconds
      setTimeout(() => this.connect(), 5000);
    };

    this.ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  addEventListener(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  removeEventListener(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  notifyListeners(eventType, payload) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach((callback) => callback(payload));
    }
  }
}

// Create WebSocket instance
export const wsService = new WebSocketService();

// Export default API instance
export default api;
